
import { rootApi } from '../rootApi';
import { APIResponse, HTTPRequestType } from '../api.types';
import { IThread, GetThreadProps, NotesFeedSource, PostCommentProps, GetNotesFeedProps, GetCommentsResponse } from './threads.types';

/**
 * A merged feed is keyed by the record it belongs to, but a note is posted to one thread — and that
 * thread can appear in feeds for records this call knows nothing about (a candidate's feed spans
 * every application, an application's feed reaches the candidate). There is no id to invalidate
 * precisely, so every write refreshes the feeds outright alongside its own thread.
 */
const invalidateThreadAndFeeds = (_r: unknown, _e: unknown, arg: { thread_id: string }) => [
  { type: 'Comments' as const, id: arg.thread_id },
  'NotesFeed' as const,
];

const threadsApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({

    getThread: builder.query<IThread, { thread_id: string }>({
      query: (data) => ({
        url: `/threads/${data.thread_id}`,
      }),
      providesTags: (_result, _error, arg) => [{ type: 'Thread', id: arg.thread_id }]
    }),

    getCommentsThread: builder.query<GetCommentsResponse, GetThreadProps>({
      query: (data) => ({
        url: `/threads/${data.thread_id}/comments?page=${data.page}&per_page=${data.per_page}`,
      }),
      providesTags: (_result, _error, arg) => [{ type: 'Comments', id: arg.thread_id }]
    }),

    /**
     * The candidate ↔ application merged notes feed. The record is named, never the threads: the
     * server resolves those and enforces that the caller's organization may read each one.
     */
    getNotesFeed: builder.query<GetCommentsResponse, GetNotesFeedProps>({
      query: (data) => {
        const params = new URLSearchParams({
          page: String(data.page),
          per_page: String(data.per_page),
        });

        if (data.application_id) {
          params.set('application_id', data.application_id);
        }

        const bases: Record<NotesFeedSource['type'], string> = {
          application: `/jobs/applications/${data.id}`,
          candidate: `/my/candidates/${data.id}`,
          deal: `/deal-flow/deals/${data.id}`,
          contact: `/organizations/contacts/${data.id}`,
          company: `/my/companies/${data.id}`,
        };

        return { url: `${bases[data.type]}/notes-feed?${params.toString()}` };
      },
      providesTags: ['NotesFeed'],
    }),

    postComment: builder.mutation<APIResponse, PostCommentProps>({
      query: (data) => ({
        url: `/threads/${data.thread_id}/comments`,
        method: HTTPRequestType.POST,
        data: {
          type: data.type,
          text: data.data.text,
          activity: data.data.activity,
          tagged_accounts: data.data.tagged_accounts,
          tagged_jobs: data.data.tagged_jobs?.map(({ id }) => ({ id })),
        },
      }),
      invalidatesTags: invalidateThreadAndFeeds
    }),
    updateComment: builder.mutation<APIResponse, { thread_id: string; comment_id: string; text: string; tagged_accounts?: any[]; tagged_jobs?: { id: string }[] }>({
      query: (data) => ({
        url: `/threads/${data.thread_id}/comments/${data.comment_id}`,
        method: HTTPRequestType.PUT,
        data: {
          text: data.text,
          tagged_accounts: data.tagged_accounts,
          tagged_jobs: data.tagged_jobs?.map(({ id }) => ({ id })),
        },
      }),
      invalidatesTags: invalidateThreadAndFeeds
    }),
    deleteComment: builder.mutation<APIResponse, { thread_id: string; comment_id: string }>({
      query: (data) => ({
        url: `/threads/${data.thread_id}/comments/${data.comment_id}`,
        method: HTTPRequestType.DELETE,
      }),
      invalidatesTags: invalidateThreadAndFeeds
    }),
  }),
});

export const {
  useGetThreadQuery,
  useGetCommentsThreadQuery,
  useLazyGetCommentsThreadQuery,
  useGetNotesFeedQuery,

  usePostCommentMutation,
  useUpdateCommentMutation,
  useDeleteCommentMutation,
} = threadsApi;
